import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button }   from './ui/button';
import { Input }    from './ui/input';
import { Label }    from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast }    from 'sonner';
import { EyeIcon, EyeSlashIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { usePageMeta, PAGE_META } from '../lib/pageMeta';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const primary      = '#6F00FF';

/* ── Floating background bubbles (same style as LoginPage) ─────────────────── */
const Bubble = ({ style }) => (
  <div
    className="absolute rounded-full pointer-events-none"
    style={{
      background: `radial-gradient(circle at 30% 30%, rgba(111,0,255,0.18), rgba(142,51,255,0.06))`,
      backdropFilter: 'blur(2px)',
      border: '1px solid rgba(111,0,255,0.12)',
      ...style,
    }}
  />
);

const Bubbles = () => (
  <>
    <Bubble style={{ width: 220, height: 220, top: '8%',  left: '-4%',  animation: 'float 8s ease-in-out infinite' }} />
    <Bubble style={{ width: 150, height: 150, top: '60%', right: '-3%', animation: 'float 10s ease-in-out infinite 2s' }} />
    <Bubble style={{ width: 100, height: 100, bottom: '10%', left: '5%', animation: 'float 7s ease-in-out infinite 1s' }} />
    <Bubble style={{ width: 80,  height: 80,  top: '35%',  right: '8%', animation: 'float 9s ease-in-out infinite 3s' }} />
  </>
);

export default function ResetPassword() {
  usePageMeta(PAGE_META.resetPassword || { title: 'Reset Password | Emax Technologies' });

  const [params]   = useSearchParams();
  const navigate   = useNavigate();
  const token      = params.get('token');

  const [password,    setPassword]    = useState('');
  const [confirm,     setConfirm]     = useState('');
  const [showPwd,     setShowPwd]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [done,        setDone]        = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error('Invalid reset link. Please request a new one.');
      navigate('/login');
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const res  = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ token, password }),
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setDone(true);
        toast.success('Password reset successfully!');
      } else {
        toast.error(data.message || 'Failed to reset password. The link may have expired.');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-[100dvh] flex flex-col overflow-hidden">
      {/* ── Background ───────────────────────────────────────────────────────── */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          background: 'radial-gradient(65% 45% at 50% 0%, rgba(111,0,255,0.11) 0%, #f4f5f8 70%)',
        }}
      />
      <Bubbles />

      {/* ── HEADER ───────────────────────────────────────────────────────────── */}
      <header
        className="relative z-10 w-full"
        style={{
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          background: 'rgba(255,255,255,0.75)',
          borderBottom: '1px solid rgba(111,0,255,0.10)',
          boxShadow: '0 2px 16px rgba(111,0,255,0.06)',
        }}
      >
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          {/* Logo + brand */}
          <div className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt="Emax Technologies"
              style={{ maxHeight: 44, width: 'auto' }}
            />
            <div className="hidden sm:block border-l border-gray-200 pl-3">
              <p className="text-xs font-semibold text-gray-700 leading-tight">Institute Management</p>
              <p className="text-[11px] text-gray-400 leading-tight">Staff Portal</p>
            </div>
          </div>

          {/* Nav link */}
          <button
            onClick={() => navigate('/login')}
            className="flex items-center gap-1.5 text-sm font-medium transition-colors hover:opacity-80"
            style={{ color: primary }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Back to Login
          </button>
        </div>
      </header>

      {/* ── MAIN CONTENT ─────────────────────────────────────────────────────── */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">

          {/* Icon badge */}
          <div className="flex justify-center mb-6">
            <div
              className="h-16 w-16 rounded-2xl flex items-center justify-center shadow-lg"
              style={{ background: `linear-gradient(135deg, ${primary}, #8E33FF)` }}
            >
              <LockClosedIcon className="h-8 w-8 text-white" />
            </div>
          </div>

          <Card className="border-0 shadow-2xl overflow-hidden">
            {/* Glass overlay */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backdropFilter:       'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                background:           'linear-gradient(to bottom right, rgba(255,255,255,0.65), rgba(255,255,255,0.30))',
                border:               '1px solid rgba(255,255,255,0.40)',
                boxShadow:            '0 10px 32px rgba(111,0,255,0.12)',
                borderRadius:         'inherit',
              }}
            />

            <CardHeader className="relative text-center pb-4">
              <CardTitle className="text-2xl font-semibold text-slate-800">
                {done ? 'Password Updated!' : 'Set New Password'}
              </CardTitle>
              <CardDescription className="text-slate-500 text-sm">
                {done
                  ? 'Your password has been reset successfully.'
                  : 'Choose a strong new password for your account.'}
              </CardDescription>
            </CardHeader>

            <CardContent className="relative">
              {done ? (
                <div className="text-center py-2">
                  <div
                    className="h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow"
                    style={{ background: 'linear-gradient(135deg,#16a34a,#22c55e)' }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-600 mb-6">
                    You can now sign in with your new password.
                  </p>
                  <Button
                    className="w-full h-11 text-white font-medium shadow-lg"
                    style={{ background: `linear-gradient(90deg, ${primary}, #8E33FF)`, boxShadow: '0 6px 16px rgba(111,0,255,0.30)' }}
                    onClick={() => navigate('/login')}
                  >
                    Go to Login
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* New password */}
                  <div className="space-y-1.5">
                    <Label htmlFor="new-password" className="text-sm font-medium text-slate-700">New Password</Label>
                    <div className="relative">
                      <Input
                        id="new-password"
                        type={showPwd ? 'text' : 'password'}
                        placeholder="At least 6 characters"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="pr-10 bg-white/70 border-white/60 focus-visible:ring-[rgba(111,0,255,0.40)]"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPwd((v) => !v)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                      >
                        {showPwd ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Confirm password */}
                  <div className="space-y-1.5">
                    <Label htmlFor="confirm-password" className="text-sm font-medium text-slate-700">Confirm Password</Label>
                    <div className="relative">
                      <Input
                        id="confirm-password"
                        type={showConfirm ? 'text' : 'password'}
                        placeholder="Repeat your new password"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        required
                        className="pr-10 bg-white/70 border-white/60 focus-visible:ring-[rgba(111,0,255,0.40)]"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirm((v) => !v)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                      >
                        {showConfirm ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Strength hint */}
                  <p className="text-[11px] text-slate-400">
                    Use at least 6 characters with a mix of letters and numbers for a strong password.
                  </p>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-11 text-white font-medium shadow-lg"
                    style={{
                      background: `linear-gradient(90deg, ${primary}, #8E33FF)`,
                      boxShadow: '0 6px 16px rgba(111,0,255,0.30)',
                    }}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Saving…
                      </span>
                    ) : 'Reset Password'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* ── FOOTER ───────────────────────────────────────────────────────────── */}
      <footer
        className="relative z-10 w-full"
        style={{
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          background: 'rgba(255,255,255,0.60)',
          borderTop: '1px solid rgba(111,0,255,0.08)',
        }}
      >
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-center sm:text-left">
            <p className="text-xs text-gray-400">
              © {new Date().getFullYear()} Emax Technologies. All rights reserved.
            </p>
            <div className="flex items-center gap-4 text-xs text-gray-400">
              <a href="https://emaxtech.com" target="_blank" rel="noreferrer" className="hover:text-gray-600 transition-colors">
                emaxtech.com
              </a>
              <span className="text-gray-200">|</span>
              <a href="mailto:info@emaxtech.com" className="hover:text-gray-600 transition-colors">
                info@emaxtech.com
              </a>
              <span className="text-gray-200">|</span>
              <span>Hyderabad, India</span>
            </div>
          </div>
        </div>
      </footer>

      {/* ── Animation keyframes ───────────────────────────────────────────────── */}
      <style>{`
        @keyframes float {
          0%   { transform: translateY(0) scale(1); }
          50%  { transform: translateY(-18px) scale(1.03); }
          100% { transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
